import React, { useContext } from 'react'
import { PRAXIS_PLUS_LIKE_DISCOUNT, propConverter } from 'chemistry'
import { getTranslations, shouldShowPraxisPlusBanner } from '../../selectors/praxisPlusSelector'
import { TranslateContext } from 'client/common/translator'
import {
  getProductListTranslations,
  getPromotionsTextsTranslations
} from 'client/fragments/listerFragment/selectors/translations'
import { fireProductClick } from 'client/fragments/listerFragment/store/middleware/gtm'
import { ConstructorProductMeta } from 'client/fragments/listerFragment/components/ConstructorMeta/ConstructorProductMeta'
import { PromotionPlusBanner } from 'client/fragments/listerFragment/components/Banner/PromotionPlusBanner'
import { Card } from 'client/common/components/Card/Card'
import { useSelector } from 'react-redux'
import { getProductPosition } from 'client/common/utils'
import ProductCardBanner from 'client/fragments/listerFragment/components/Products/ProductCardBanner'
import { ListProductPlaceholder } from 'client/common/components/ListProductPlaceholder'
import { getImageOptimizationProps } from 'client/common/components/Card/imageProps'
import classNames from 'classnames'

export const productBannersContainerId = 'listing-product-widgets'

const Products = ({ visible }) => {
  const loading = useSelector(it => it.data.loading)
  const showNewCardsGrid = useSelector(({ data }) => data?.flags?.showNewCardsGrid)

  if (loading)
    return (
      <div
        className={classNames('mxd-product-area-new-lister-view', {
          'mxd-product-area-new-lister-view-new-cards-grid': showNewCardsGrid
        })}
      >
        <ListProductPlaceholder />
      </div>
    )
  if (!visible)
    return (
      <div
        className={classNames('mxd-product-area-new-lister-view', {
          'mxd-product-area-new-lister-view-new-cards-grid': showNewCardsGrid
        })}
      />
    )
  return <ProductsGrid />
}

const ProductsGrid = () => {
  const {
    format,
    lang,
    userId,
    categoryCode,
    products: productsData,
    realViewSize,
    searchResultType,
    page,
    cardBanners,
    flags
  } = useSelector(state => state.data)
  const showAvailabilityStatusMultiple = useSelector(({ data }) => data?.flags?.showAvailabilityStatusMultiple)
  const showNewCardsGrid = useSelector(({ data }) => data?.flags?.showNewCardsGrid)
  const showBricoPlanitPriceFlag = useSelector(({ data }) => data?.flags?.showBricoPlanitPrice)
  const showOwnBrandCommunicationFlag = useSelector(({ data }) => data?.flags?.showOwnBrandCommunication)
  const t = useContext(TranslateContext)
  let products = productsData?.collection || []
  if (!products.length) return null
  const isLoggedIn = !!userId
  let praxisPlusText = false
  let showPraxisPlusBanner = false

  products = products.map((props, i) => {
    const productListTranslations = getProductListTranslations(t)
    if (props.unitName) {
      const per = lang === 'fr' ? 'par' : 'per'
      const unitTranslated = t(`pim.units.${props.unitName}`)
      productListTranslations.units = `${per} ${unitTranslated}`
    }

    const cardProps = {
      ...props,
      format,
      lang,
      isDetail: false,
      t: productListTranslations,
      texts: getPromotionsTextsTranslations(t),
      showPraxisPlusBanner: false,
      showSoldByProductCards: flags?.showSoldByProductCards,
      translate: t
    }

    const fullPriceProps = propConverter(cardProps)
    const promotionType = fullPriceProps.type

    if (shouldShowPraxisPlusBannerOneTimeInit(praxisPlusText, promotionType)) {
      praxisPlusText = getTranslations(format, lang)
      showPraxisPlusBanner = shouldShowPraxisPlusBanner(isLoggedIn)
    }

    const position = getProductPosition(i + 1, page.page, realViewSize)
    const imgProps = getImageOptimizationProps(i)
    const isSponsored = props.sponsored

    return (
      <div
        key={`product-${props.ean}`}
        onClick={() => {
          fireProductClick(categoryCode, { ...cardProps, index: position }, searchResultType)
        }}
        {...(isSponsored ? { 'data-cnstrc-sl-campaign-id': 'ad_id' } : {})}
      >
        <ConstructorProductMeta {...cardProps} />
        <Card
          {...cardProps}
          imgProps={imgProps}
          fullPriceProps={fullPriceProps}
          categoryCode={categoryCode}
          isSponsored={isSponsored}
          showAvailabilityStatusMultiple={showAvailabilityStatusMultiple}
          showNewCardsGrid={showNewCardsGrid}
          showBricoPlanitPriceFlag={showBricoPlanitPriceFlag}
          showOwnBrandCommunicationFlag={showOwnBrandCommunicationFlag}
        />
      </div>
    )
  })

  products = applyBanners({ products, cardBanners, realViewSize })

  if (showPraxisPlusBanner && praxisPlusText) {
    const promotionPlusBanner = (
      <PromotionPlusBanner key='praxis-plus-banner' format={format} lang={lang} praxisPlusText={praxisPlusText} />
    )
    products.splice(3, 0, promotionPlusBanner)
  }

  return (
    <div
      className={classNames('mxd-product-area-new-lister-view', {
        'mxd-product-area-new-lister-view-new-cards-grid': showNewCardsGrid
      })}
    >
      {products}
    </div>
  )
}

const PRODUCTS_MAX_SIZE = 23
export const applyBanners = ({ cardBanners, products, realViewSize }) => {
  if (!cardBanners?.length) return products

  cardBanners.forEach((banner, index) => {
    const { position: bannerPos } = banner
    if (bannerPos <= 0) return
    const position = bannerPos - 1
    const bannerElement = <ProductCardBanner {...banner} />

    if (position < realViewSize - 1 && position < products.length) {
      products.splice(position, 0, bannerElement)
    } else if (position === PRODUCTS_MAX_SIZE && products.length === PRODUCTS_MAX_SIZE) {
      products.push(bannerElement)
    } else if (index === 0) {
      if (position === realViewSize - 1) {
        products.push(bannerElement)
      } else {
        products.splice(position, 0, bannerElement)
      }
    }
  })

  return products
}

const shouldShowPraxisPlusBannerOneTimeInit = (praxisPlusText, promotionType) =>
  !praxisPlusText && [PRAXIS_PLUS_LIKE_DISCOUNT].includes(promotionType)

export default Products
